/**
 * Pilot queries and mutations.
 *
 * Every mutation returns the whole updated pilot, so each one seeds the detail
 * cache from its own response rather than refetching. That is not just a saved
 * request: the KPI table, the milestones and the blockers all carry the pilot's
 * new version, and a refetch racing the response could hand the form a version
 * that is already stale — producing a 409 on the user's very next save.
 *
 * The lists are invalidated rather than seeded, because a change can move a
 * pilot in or out of the `overdue` and `blocked` filters.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.2
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type {
  CreateBlockerRequest,
  CreatePilotRequest,
  ListPilotsQuery,
  PilotDetail,
  PilotTimeline,
  ReplaceKpisRequest,
  ReplaceMilestonesRequest,
  UpdatePilotRequest,
} from '@nexus/contracts';

import { queryKeys } from '../../../lib/query-keys';

import * as pilotsApi from './pilots.api';
import type { PilotPage } from './pilots.api';

export function usePilots(query: ListPilotsQuery): UseQueryResult<PilotPage> {
  return useQuery({
    queryKey: queryKeys.pilots.list(query),
    queryFn: () => pilotsApi.listPilots(query),
  });
}

/** The portfolio timeline / Gantt (F16). */
export function usePilotTimeline(): UseQueryResult<PilotTimeline> {
  return useQuery({
    queryKey: queryKeys.pilots.timeline(),
    queryFn: () => pilotsApi.getPilotTimeline(),
  });
}

export function usePilot(id: string): UseQueryResult<PilotDetail> {
  return useQuery({
    queryKey: queryKeys.pilots.detail(id),
    queryFn: () => pilotsApi.getPilot(id),
  });
}

/**
 * Seeds the detail cache and invalidates the lists.
 *
 * Shared by every mutation below, because they all return the same shape and
 * all have the same cache consequences. Written once so a new mutation cannot
 * forget half of it.
 */
function useApplyPilot(): (pilot: PilotDetail) => void {
  const queryClient = useQueryClient();

  return (pilot: PilotDetail) => {
    queryClient.setQueryData(queryKeys.pilots.detail(pilot.id), pilot);
    void queryClient.invalidateQueries({ queryKey: queryKeys.pilots.lists() });
  };
}

export function useCreatePilot(): UseMutationResult<
  PilotDetail,
  Error,
  CreatePilotRequest
> {
  const apply = useApplyPilot();

  return useMutation({
    mutationFn: (body: CreatePilotRequest) => pilotsApi.createPilot(body),
    onSuccess: apply,
  });
}

export function useUpdatePilot(
  id: string,
): UseMutationResult<PilotDetail, Error, UpdatePilotRequest> {
  const apply = useApplyPilot();

  return useMutation({
    mutationFn: (body: UpdatePilotRequest) => pilotsApi.updatePilot(id, body),
    onSuccess: apply,
  });
}

/** Replaces the whole KPI table (PRD §5.4). */
export function useReplaceKpis(
  id: string,
): UseMutationResult<PilotDetail, Error, ReplaceKpisRequest> {
  const apply = useApplyPilot();

  return useMutation({
    mutationFn: (body: ReplaceKpisRequest) => pilotsApi.replaceKpis(id, body),
    onSuccess: apply,
  });
}

/** Replaces the whole milestone list. */
export function useReplaceMilestones(
  id: string,
): UseMutationResult<PilotDetail, Error, ReplaceMilestonesRequest> {
  const apply = useApplyPilot();

  return useMutation({
    mutationFn: (body: ReplaceMilestonesRequest) => pilotsApi.replaceMilestones(id, body),
    onSuccess: apply,
  });
}

/** Appends one blocker. */
export function useAddBlocker(
  id: string,
): UseMutationResult<PilotDetail, Error, CreateBlockerRequest> {
  const apply = useApplyPilot();

  return useMutation({
    mutationFn: (body: CreateBlockerRequest) => pilotsApi.addBlocker(id, body),
    onSuccess: apply,
  });
}

/** Resolves or reopens one blocker. */
export function useResolveBlocker(
  id: string,
): UseMutationResult<
  PilotDetail,
  Error,
  { blockerId: string; resolved: boolean; version: number }
> {
  const apply = useApplyPilot();

  return useMutation({
    mutationFn: ({
      blockerId,
      resolved,
      version,
    }: {
      blockerId: string;
      resolved: boolean;
      version: number;
    }) => pilotsApi.resolveBlocker(id, blockerId, { resolved, version }),
    onSuccess: apply,
  });
}
